import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, Plus, Download, RefreshCw, Clock, ShieldAlert, FileSpreadsheet } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import ReportComposer from "../components/ReportComposer";
import ReportReview from "../components/ReportReview";

const STATUS_BADGE = {
  pending_approval: "sw-badge-pending",
  approved: "sw-badge-approved",
  rejected: "sw-badge-rejected",
  revoked: "sw-badge-rejected",
  expired: "sw-badge-neutral",
};

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "—");

export default function Reports() {
  const { user, hasPerm } = useAuth();
  const { t, lang } = useI18n();
  const [searchParams] = useSearchParams();
  const isHR = ["hr", "admin"].includes(user?.role);
  const canRequest = hasPerm("reports.request") || isHR;
  const [tab, setTab] = useState(searchParams.get("tab") === "approvals" && true ? "approvals" : "mine");
  const [catalog, setCatalog] = useState(null);
  const [requests, setRequests] = useState([]);
  const [runs, setRuns] = useState([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [reviewReq, setReviewReq] = useState(null);
  const [busyRun, setBusyRun] = useState(null);

  const load = useCallback(async () => {
    try {
      const [rq, rn] = await Promise.all([api.get("/reports/requests"), api.get("/reports/runs")]);
      setRequests(rq.data);
      setRuns(rn.data);
    } catch (e) { /* 403 for users without perm */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canRequest) return;
    api.get("/reports/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, [canRequest]);

  const L = (o) => (lang === "pt" ? o?.label_pt : o?.label_en) || "";
  const entityLabel = (key) => L(catalog?.entities?.find((e) => e.key === key)) || key;
  const fieldLabel = (ekey, fkey) => L(catalog?.entities?.find((e) => e.key === ekey)?.fields?.find((f) => f.key === fkey)) || fkey;

  const runNow = async (rid) => {
    setBusyRun(rid);
    try {
      await api.post(`/reports/requests/${rid}/run-now`);
      toast.success(t("reports.run_now") + " ✓");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusyRun(null); }
  };

  const download = async (runId, fmt) => {
    try {
      const resp = await api.get(`/reports/runs/${runId}/download?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const m = (resp.headers["content-disposition"] || "").match(/filename="(.+)"/);
      a.download = m ? m[1] : `report.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const revoke = async (rid) => {
    if (!window.confirm(t("reports.revoke") + "?")) return;
    try {
      await api.post(`/reports/requests/${rid}/revoke`);
      toast.success(t("reports.status_revoked"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
  };

  const myRequests = requests.filter((r) => r.requester_id === user?.id);
  const pendingAll = requests.filter((r) => r.status === "pending_approval");

  if (!canRequest) {
    return (
      <div data-testid="reports-restricted" className="sw-card text-center py-14">
        <ShieldAlert size={32} className="mx-auto text-[#94a3b8] mb-3" />
        <p className="text-[14px] text-[#747474]">{t("reports.restricted")}</p>
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="sw-heading text-[28px] flex items-center gap-2"><BarChart3 size={26} className="text-[#475569]" /> {t("nav.reports")}</h1>
          <p className="text-[14px] text-[#747474] mt-1">{t("reports.page_sub")}</p>
        </div>
        {canRequest && !composerOpen && (
          <button data-testid="reports-new-btn" onClick={() => setComposerOpen(true)} className="sw-btn-primary flex items-center gap-2">
            <Plus size={16} /> {t("reports.new_request")}
          </button>
        )}
      </div>

      {composerOpen && catalog && (
        <ReportComposer catalog={catalog} onCancel={() => setComposerOpen(false)}
          onCreated={() => { setComposerOpen(false); load(); }} />
      )}

      {!composerOpen && (<>
      <div className="flex gap-1 bg-white rounded-full p-1 w-fit border border-[#e2e8f0]">
        <button data-testid="reports-tab-mine" onClick={() => setTab("mine")} className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all ${tab === "mine" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>{t("reports.tab_mine")}</button>
        {isHR && (
          <button data-testid="reports-tab-approvals" onClick={() => setTab("approvals")} className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all flex items-center gap-1.5 ${tab === "approvals" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>
            {t("reports.tab_approvals")}
            {pendingAll.length > 0 && <span className="bg-amber-400 text-[#1e293b] text-[11px] font-bold rounded-full px-1.5">{pendingAll.length}</span>}
          </button>
        )}
        {isHR && (
          <button data-testid="reports-tab-governance" onClick={() => setTab("governance")} className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all ${tab === "governance" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>{t("reports.tab_governance")}</button>
        )}
      </div>

      {tab === "mine" && (
        <div className="space-y-4">
          {myRequests.length === 0 && (
            <div className="sw-card text-center text-[14px] text-[#747474] py-10" data-testid="reports-empty">{t("reports.no_reports")}</div>
          )}
          {myRequests.map((r) => (
            <ReportCard key={r.id} r={r} runs={runs.filter((x) => x.request_id === r.id)} t={t} lang={lang}
              entityLabel={entityLabel} fieldLabel={fieldLabel}
              onRunNow={() => runNow(r.id)} busy={busyRun === r.id} onDownload={download} />
          ))}
        </div>
      )}

      {tab === "approvals" && isHR && (
        <div className="space-y-3">
          {pendingAll.length === 0 && <div className="sw-card text-center text-[14px] text-[#747474] py-10" data-testid="reports-no-pending">{t("reports.no_pending")}</div>}
          {pendingAll.map((r) => (
            <button key={r.id} data-testid={`report-pending-${r.id}`} onClick={() => setReviewReq(r)}
              className="sw-card w-full text-left flex flex-wrap items-center gap-3 hover:border-[#475569] transition-all">
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold text-[15px] text-[#1e293b]">{r.title}</div>
                <div className="text-[13px] text-[#747474]">{t("reports.requester")}: {r.requester_name} · {fmtDate(r.created_at)}</div>
                <div className="text-[12px] text-[#94a3b8] mt-1">{r.datasets.map((d) => entityLabel(d.entity)).join(" · ")}</div>
              </div>
              {r.has_confidential && (
                <span className="flex items-center gap-1 text-[12px] font-semibold text-rose-600 bg-rose-50 rounded-full px-3 py-1">
                  <ShieldAlert size={13} /> {t("reports.sens_confidential")}
                </span>
              )}
              <span className="sw-badge sw-badge-pending">{t("reports.status_pending_approval")}</span>
            </button>
          ))}
        </div>
      )}

      {tab === "governance" && isHR && (
        <div className="sw-card overflow-x-auto">
          <p className="text-[13px] text-[#747474] mb-3">{t("reports.gov_intro")}</p>
          <table className="w-full text-[13px]" data-testid="reports-gov-table">
            <thead>
              <tr className="text-left text-[#94a3b8] border-b border-[#e2e8f0]">
                <th className="py-2 pr-3">{t("reports.title_label")}</th>
                <th className="py-2 pr-3">{t("reports.requester")}</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">{t("reports.frequency")}</th>
                <th className="py-2 pr-3">{t("reports.next_run")}</th>
                <th className="py-2 pr-3">{t("reports.expires")}</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} className="border-b border-[#f1f5f9]">
                  <td className="py-2.5 pr-3 font-medium text-[#1e293b]">{r.title}</td>
                  <td className="py-2.5 pr-3">{r.requester_name}</td>
                  <td className="py-2.5 pr-3"><span className={`sw-badge ${STATUS_BADGE[r.status] || "sw-badge-neutral"}`}>{t(`reports.status_${r.status}`)}</span></td>
                  <td className="py-2.5 pr-3">{t(`reports.freq_${r.schedule?.frequency || "once"}`)}</td>
                  <td className="py-2.5 pr-3">{fmtDate(r.next_run_at)}</td>
                  <td className="py-2.5 pr-3">{r.expires_at ? fmtDate(r.expires_at) : t("reports.never_expires")}</td>
                  <td className="py-2.5 text-right">
                    {r.status === "approved" && (
                      <button data-testid={`gov-revoke-${r.id}`} onClick={() => revoke(r.id)} className="text-[12px] font-semibold text-rose-600 hover:underline">{t("reports.revoke")}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>)}

      {reviewReq && catalog && (
        <ReportReview request={reviewReq} catalog={catalog} onClose={() => setReviewReq(null)} onDecided={() => { setReviewReq(null); load(); }} />
      )}
    </div>
  );
}

function ReportCard({ r, runs, t, lang, entityLabel, fieldLabel, onRunNow, busy, onDownload }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="sw-card" data-testid={`report-card-${r.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[15px] text-[#1e293b]">{r.title}</span>
            <span className={`sw-badge ${STATUS_BADGE[r.status] || "sw-badge-neutral"}`}>{t(`reports.status_${r.status}`)}</span>
            {r.has_confidential && <span className="flex items-center gap-1 text-[11px] font-semibold text-rose-600"><ShieldAlert size={12} /> {t("reports.sens_confidential")}</span>}
          </div>
          <div className="text-[13px] text-[#747474] mt-1">
            {r.datasets.map((d) => entityLabel(d.entity)).join(" · ")} · {t(`reports.freq_${r.schedule?.frequency || "once"}`)}
          </div>
          {r.status === "approved" && (
            <div className="flex flex-wrap gap-4 text-[12px] text-[#94a3b8] mt-1.5">
              <span className="flex items-center gap-1"><Clock size={12} /> {t("reports.next_run")}: {fmtDate(r.next_run_at)}</span>
              <span>{t("reports.expires")}: {r.expires_at ? fmtDate(r.expires_at) : t("reports.never_expires")}</span>
            </div>
          )}
          {r.status === "pending_approval" && <div className="text-[12px] text-amber-600 mt-1.5">{t("reports.pending_hint")}</div>}
          {r.status === "rejected" && r.rejected_reason && (
            <div className="text-[12px] text-rose-600 mt-1.5">{t("reports.rejected_reason")}: "{r.rejected_reason}"</div>
          )}
          {(r.removed_fields || []).length > 0 && (
            <div className="mt-2 text-[12px] text-[#747474]" data-testid={`report-removed-${r.id}`}>
              {t("reports.removed_fields_note")}: {r.removed_fields.map((rf) => fieldLabel(rf.entity, rf.field)).join(", ")}
              {r.approval_comment ? ` — "${r.approval_comment}"` : ""}
            </div>
          )}
        </div>
        {r.status === "approved" && (
          <button data-testid={`report-run-now-${r.id}`} onClick={onRunNow} disabled={busy} className="sw-btn-secondary flex items-center gap-1.5 text-[13px]">
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {t("reports.run_now")}
          </button>
        )}
      </div>
      {runs.length > 0 && (
        <div className="mt-4 border-t border-[#f1f5f9] pt-3">
          <button onClick={() => setOpen(!open)} className="text-[13px] font-semibold text-[#475569] flex items-center gap-1.5" data-testid={`report-runs-toggle-${r.id}`}>
            <FileSpreadsheet size={14} /> {t("reports.runs_history")} · {runs.length}
          </button>
          {open && (
            <div className="mt-2 space-y-1.5">
              {runs.map((run) => (
                <div key={run.id} className="flex flex-wrap items-center gap-3 text-[13px] bg-[#f8fafc] rounded-[10px] px-3 py-2">
                  <span className="text-[#1e293b]">{new Date(run.generated_at).toLocaleString(lang === "pt" ? "pt-BR" : "en-US")}</span>
                  <span className="text-[#94a3b8]">{t(`reports.trigger_${run.trigger}`)}</span>
                  <span className="text-[#94a3b8]">{Object.values(run.row_counts || {}).reduce((a, b) => a + b, 0)} {t("reports.rows")}</span>
                  {run.status === "success" ? (
                    <span className="ml-auto flex gap-2">
                      <button data-testid={`run-download-xlsx-${run.id}`} onClick={() => onDownload(run.id, "xlsx")} className="flex items-center gap-1 text-[12px] font-semibold text-emerald-700 hover:underline"><Download size={12} /> {t("reports.download_xlsx")}</button>
                      <button data-testid={`run-download-csv-${run.id}`} onClick={() => onDownload(run.id, "csv")} className="flex items-center gap-1 text-[12px] font-semibold text-[#475569] hover:underline"><Download size={12} /> {t("reports.download_csv")}{run.files?.csv_kind === "zip" ? " (zip)" : ""}</button>
                    </span>
                  ) : (
                    <span className="ml-auto sw-badge sw-badge-rejected" title={run.error || ""}>{t("reports.run_failed")}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
